import React, { useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useCourseActions } from 'src/store/course/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { RootState } from 'src/store/reducers/state';
import { useNavigate } from 'react-router-dom';
import { useVideosActions } from 'src/store/videos/actions';
import DeleteContentModal from './DeleteContentModal';
import { successMsg } from 'src/components/utilities/utils';
const CourseSequence = ({ setChapterAddFlag }: any) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const courseAction = useCourseActions(dispatch);
  const videoActions = useVideosActions(dispatch);
  const [data, setData] = useState<any>([]);
  const { id } = useParams<string>();
  const [sequenceUpdated, setSequenceUpdated] = useState<boolean>(false);
  const [contentId, setContentId] = useState<string>('');
  const [contentType, setContentType] = useState<number>(0);
  const [contentName, setContentName] = useState<string>('');
  const [activeDropdown, setActiveDropdown] = useState<{
    type: string;
    id: string | null;
  }>({
    type: '',
    id: null
  });

  const { courseSequences, isChapterAdded, isCheckpointAdded, isVideoDeleted, isChapterDeleted, isCheckpointDeleted } = useSelector((state: RootState) => {
    return {
      courseSequences: state.course.courseSequences,
      isChapterAdded: state.course.isChapterAdded,
      isCheckpointAdded: state.course.isCheckpointAdded,
      isVideoDeleted: state.videos.isVideoDeleted,
      isChapterDeleted: state.course.isChapterDeleted,
      isCheckpointDeleted: state.course.isCheckpointDeleted
    };
  });

  const onDragEnd = (result: any) => {
    const { source, destination, type } = result;

    if (!destination) return;

    if (type === 'CHAPTER') {
      const reordered = Array.from(data.contents);
      const [removed] = reordered.splice(source.index, 1);
      reordered.splice(destination.index, 0, removed);

      setData({ ...data, contents: reordered });
      setSequenceUpdated(true);
      return;
    }

    const sourceIndex = source.index;
    const destinationIndex = destination.index;

    const sourceDroppableId = source.droppableId;
    const destinationDroppableId = destination.droppableId;

    const updatedContents = data.contents.map((chapter: any) => ({
      ...chapter,
      contentOrders: [...(chapter.contentOrders || [])]
    }));

    const sourceChapter = updatedContents.find(
      (chapter: any) =>
        chapter.chapterId === sourceDroppableId ||
        (!chapter.chapterId && sourceDroppableId === 'null')
    );

    const destinationChapter = updatedContents.find(
      (chapter: any) =>
        chapter.chapterId === destinationDroppableId ||
        (!chapter.chapterId && destinationDroppableId === 'null')
    );

    if (sourceChapter && destinationChapter) {
      const [movedItem] = sourceChapter.contentOrders.splice(sourceIndex, 1);
      destinationChapter.contentOrders.splice(destinationIndex, 0, movedItem);
      setData({ ...data, contents: updatedContents });
      setSequenceUpdated(true);
    }
  };

  useEffect(() => {
    if (id) {
      courseAction.getCourseSequencesRequest(id);
    }
  }, [isChapterAdded, isCheckpointAdded]);

  useEffect(() => {
    if (isVideoDeleted) {
      videoActions.resetVideoLibraryRequest()
      setContentId('')
      setContentType(0)
      setContentName('')
      successMsg('success')
      if (id) {
        courseAction.getCourseSequencesRequest(id);
      }
    }
    if (isChapterDeleted || isCheckpointDeleted) {
      courseAction.resetCourseStateRequest()
      setContentId('')
      setContentType(0)
      setContentName('')
      successMsg('success')
      if (id) {
        courseAction.getCourseSequencesRequest(id);
      }
    }
  }, [isVideoDeleted, isChapterDeleted, isCheckpointDeleted])
  useEffect(() => {
    if (courseSequences) {
      setData(courseSequences);
    }
  }, [courseSequences]);

  useEffect(() => {
    if (sequenceUpdated) {
      if (id) {
        courseAction.saveCourseSequencesRequest(data);
      }
      setSequenceUpdated(false);
    }
  }, [sequenceUpdated]);

  const toggleDropdown = (type: 'chapter' | 'content', id: string | null) => {
    setActiveDropdown((prev) =>
      prev.type === type && prev.id === id ? { type: '', id: null } : { type, id }
    );
  };

  const handleEditContent = (content: any) => {
    if (content.contentTypes == 1 || content.contentTypes == 2) {
      window.open(`/videos/${content.id}`, '_blank');
    } else {
      window.open(`/checkpoint/${id}`, '_blank');
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (!target.closest('.dropdown-menu')) {
      setActiveDropdown({ type: '', id: null });
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getContentIcon = (contentType: number) => {
    if (contentType === 2) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
          xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
            stroke="#6c757d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <polyline points="14,2 14,8 20,8"
            stroke="#6c757d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <line x1="16" y1="13" x2="8" y2="13"
            stroke="#6c757d" strokeWidth="2" strokeLinecap="round" />
          <line x1="16" y1="17" x2="8" y2="17"
            stroke="#6c757d" strokeWidth="2" strokeLinecap="round" />
          <polyline points="10,9 9,9 8,9"
            stroke="#6c757d" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    }

    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
        xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
        <polygon points="5,3 19,12 5,21"
          fill="#6c757d" stroke="#6c757d" strokeWidth="1.5"
          strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  };
  return (
    <>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="chapters" type="CHAPTER">
          {(provided) => (
            <div className="course-page" ref={provided.innerRef} {...provided.droppableProps}>
              {data &&
                data.contents?.map((chapter: any, chapterIndex: number) => (
                  <Draggable
                    key={chapter.chapterId || `chapter-${chapterIndex}`}
                    draggableId={chapter.chapterId?.toString() || `chapter-${chapterIndex}`}
                    index={chapterIndex}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{
                          ...provided.draggableProps.style,
                          margin: '10px 0'
                        }}>
                        <Droppable
                          key={chapter.chapterId || 'null'}
                          droppableId={chapter.chapterId || 'null'}>
                          {(innerProvided) => (
                            <div
                              className="chapter"
                              ref={innerProvided.innerRef}
                              {...innerProvided.droppableProps}
                              style={{
                                margin: '0',
                                padding: '10px',
                                border:
                                  chapter.chapterId && chapter.chapterName
                                    ? '1px solid gray'
                                    : 'none',
                                borderRadius: '4px',
                                position: 'relative'
                              }}>
                              {chapter.chapterId && chapter.chapterName && (
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                  }}>
                                  <h4>{chapter.chapterName}</h4>
                                  <button
                                    onClick={() => toggleDropdown('chapter', chapter.chapterId)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                      fontSize: '20px'
                                    }}>
                                    ⋮
                                  </button>
                                </div>
                              )}

                              {activeDropdown.type === 'chapter' &&
                                activeDropdown.id === chapter.chapterId && (
                                  <div
                                    className="dropdown-menu"
                                    style={{
                                      position: 'absolute',
                                      top: '40px',
                                      right: '10px',
                                      background: 'white',
                                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                      borderRadius: '4px',
                                      zIndex: 100
                                    }}>
                                    <ul style={{ listStyle: 'none', margin: 0, padding: '10px' }}>
                                      <li
                                        data-bs-toggle="modal"
                                        data-bs-target="#AddChapterModel"
                                        onClick={() => {
                                          setChapterAddFlag('edit'),
                                            courseAction.getSingleChapterRequest(chapter.chapterId);
                                        }}>
                                        <span className="dropdown-item flex-items cursor-pointer">
                                          <i className="fas fa-edit w-6"></i>
                                          <span>Edit Chapter</span>
                                        </span>
                                      </li>
                                      <li onClick={() => {
                                        setContentId(chapter.chapterId)
                                        setContentType(4)
                                        setContentName(chapter.chapterName)
                                      }}
                                        data-bs-toggle="modal"
                                        data-bs-target="#deleteContentModal"
                                      >
                                        <span className="dropdown-item flex-items cursor-pointer text-red-500">
                                          <i className="fas fa-trash w-6"></i>
                                          <span>Delete Chapter</span>
                                        </span>
                                      </li>
                                    </ul>
                                  </div>
                                )}

                              {chapter.contentOrders &&
                                chapter.contentOrders.map((item: any, idx: number) => (
                                  <Draggable
                                    key={item.id}
                                    draggableId={item?.id?.toString()}
                                    index={idx}>
                                    {(provided) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        style={{
                                          ...provided.draggableProps.style,
                                          padding: '10px',
                                          margin: '5px 0',
                                          backgroundColor: '#f8f9fa',
                                          border: '1px solid #ddd',
                                          borderRadius: '4px',
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center'
                                        }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                          {getContentIcon(item.contentTypes)}
                                          {item.name}
                                        </span>
                                        <button
                                          onClick={() => toggleDropdown('content', item.id)}
                                          style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: '20px'
                                          }}>
                                          ⋮
                                        </button>

                                        {activeDropdown.type === 'content' &&
                                          activeDropdown.id === item.id && (
                                            <div
                                              className="dropdown-menu"
                                              style={{
                                                position: 'absolute',
                                                top: '40px',
                                                right: '10px',
                                                background: 'white',
                                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                                borderRadius: '4px',
                                                zIndex: 100
                                              }}>
                                              <ul
                                                style={{
                                                  listStyle: 'none',
                                                  margin: 0,
                                                  padding: '10px'
                                                }}>
                                                <li
                                                  onClick={() => handleEditContent(item)}>
                                                  <span className="dropdown-item flex-items cursor-pointer">
                                                    <i className="fas fa-edit w-6"></i>
                                                    <span>Edit Content</span>
                                                  </span>
                                                </li>
                                                <li onClick={() => {
                                                  setContentId(item.id)
                                                  setContentType(item.contentTypes)
                                                  setContentName(item.name)
                                                }}
                                                  data-bs-toggle="modal"
                                                  data-bs-target="#deleteContentModal"
                                                >
                                                  <span className="dropdown-item flex-items cursor-pointer text-red-500">
                                                    <i className="fas fa-trash w-6"></i>
                                                    <span>Delete Content</span>
                                                  </span>
                                                </li>
                                              </ul>
                                            </div>
                                          )}
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                              {innerProvided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </div>
                    )}
                  </Draggable>
                ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <DeleteContentModal contentId={contentId} contentType={contentType} contentName={contentName} />
    </>
  );
};

export default CourseSequence;
